import { useCallback, useEffect, useState } from "react";
import { api, type PublicOrder } from "../api";
import { exclusionLabel, money, Spinner } from "../ui";

/** Клиентские статусы из п. 9.1 ТЗ — тем языком, каким их читает гость. */
const STEPS = [
  { status: "paid", title: "Оплата прошла" },
  { status: "accepted", title: "Заказ принят" },
  { status: "cooking", title: "Готовится" },
  { status: "ready", title: "Готов" },
  { status: "picked_up", title: "Несём к вашей лавочке" },
  { status: "delivered", title: "Доставлен" }
] as const;

const REFUND_LABELS: Record<string, string> = {
  partial_refund: "Часть заказа отменена, возврат оформлен",
  full_refund: "Заказ отменён, возврат оформлен",
  refund_processing: "Возврат в обработке",
  refund_error: "С возвратом возникла заминка, мы уже разбираемся"
};

export function OrderScreen({ token }: { token: string }) {
  const [order, setOrder] = useState<PublicOrder | null>(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      setOrder(await api.get<PublicOrder>(`/api/public/orders/${encodeURIComponent(token)}`));
    } catch {
      setMissing(true);
    }
  }, [token]);

  useEffect(() => {
    void load();

    // Публичный поток не содержит чужих данных: страница узнаёт, что
    // что-то изменилось, и перечитывает свой заказ (п. 29 ТЗ).
    const stream = new EventSource("/api/public/events");
    stream.addEventListener("state", () => void load());

    return () => stream.close();
  }, [load]);

  if (missing) {
    return (
      <div className="center">
        <h1>Заказ не найден</h1>
        <p className="muted">Проверьте ссылку — возможно, она скопирована не полностью.</p>
        <a className="wide-btn ghost" href="/" style={{ display: "grid", placeItems: "center", padding: "0 24px" }}>
          Открыть меню
        </a>
      </div>
    );
  }

  if (!order) return <Spinner text="Загружаем заказ…" />;

  const currentIndex = STEPS.findIndex(step => step.status === order.status);
  const cancelled = order.status === "cancelled";

  return (
    <div className="shell">
      <header className="app-bar">
        <a className="brand" href="/">
          <span className="brand-mark">ПФ</span>
          <span>
            <b>Заказ №{order.number}</b>
            <small>лавочка №{order.benchId}</small>
          </span>
        </a>
      </header>

      <main className="page">
        <div className="status-page">
          <section className={`status-hero ${cancelled ? "" : ""}`}>
            {/* Номер крупно и первым: на лавочке может сидеть компания, где
                каждый заказал сам, и курьер называет именно номер. */}
            {!cancelled ? (
              <p className="order-number" aria-label={`Ваш номер заказа ${order.number}`}>
                <small>ваш номер</small>
                <b>{order.number}</b>
              </p>
            ) : null}

            <h1>{cancelled ? "Заказ отменён" : (STEPS[currentIndex]?.title ?? "Ожидает оплаты")}</h1>
            {cancelled ? (
              <p className="eta">{order.cancelReason || "Заказ отменён сотрудником"}</p>
            ) : order.status === "delivered" ? (
              <p className="eta">Приятного аппетита!</p>
            ) : (
              <p className="eta">Примерное время ожидания: {order.etaMinutes}–{order.etaMinutes + 5} минут</p>
            )}
          </section>

          {!cancelled ? (
            <div className="steps">
              {STEPS.map((step, index) => (
                <div
                  className={`step ${index < currentIndex ? "done" : ""} ${index === currentIndex ? "current" : ""}`}
                  key={step.status}
                >
                  <i>{index < currentIndex ? "✓" : index + 1}</i>
                  {step.title}
                </div>
              ))}
            </div>
          ) : null}

          {order.corrected && !cancelled ? (
            <p className="alert warn">Состав заказа изменён по согласованию с вами.</p>
          ) : null}

          {order.refundAmount ? (
            <div className="alert good">
              <div>{REFUND_LABELS[order.refundStatus ?? ""] ?? "Возврат оформлен"}: {money(order.refundAmount)}</div>
              <div style={{ fontWeight: 400, marginTop: 4 }}>Срок зачисления зависит от вашего банка.</div>
            </div>
          ) : null}

          <section className="panel">
            <h3>Состав</h3>
            {order.lines.map((line, index) => (
              <div key={index} style={{ display: "grid", gap: 2 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <b>
                    {line.title} × {line.qty}
                  </b>
                  <span className="num">{money(line.total)}</span>
                </div>
                {line.modifiers.length > 0 ? (
                  <small className="muted">
                    {line.modifiers
                      .map(modifier =>
                        modifier.exclusion
                          ? exclusionLabel(modifier.title)
                          : `+ ${modifier.title}${modifier.qty > 1 ? ` ×${modifier.qty}` : ""}`
                      )
                      .join(" · ")}
                  </small>
                ) : null}
              </div>
            ))}

            <div className="totals" style={{ marginTop: 6 }}>
              {order.promotionDiscount ? (
                <div className="row-save">
                  <span>Акции</span>
                  <span>−{money(order.promotionDiscount)}</span>
                </div>
              ) : null}
              {order.discount ? (
                <div className="row-save">
                  <span>Промокод {order.promo}</span>
                  <span>−{money(order.discount)}</span>
                </div>
              ) : null}
              <div className="row-muted">
                <span>Доставка к лавочке</span>
                <span>бесплатно</span>
              </div>
              <div className="row-grand">
                <span>Итого</span>
                <span>{money(order.total)}</span>
              </div>
            </div>
          </section>

          {order.comment ? (
            <section className="panel">
              <h3>Ваш комментарий</h3>
              <p className="muted">{order.comment}</p>
            </section>
          ) : null}

          <p className="tiny muted" style={{ textAlign: "center" }}>
            Страница обновляется сама — держите её открытой.
          </p>
        </div>
      </main>
    </div>
  );
}
